import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { normalizeAccessState } from './campaignAccessNormalize';
const CAMPAIGN_KEYS = [
  'live',
  'stopping',
  'stopped',
  'stopFailed',
  'completed',
  'archived',
  'total',
];
const ACTIONABLE_KEYS = [
  'outbox',
  'blockedOutbox',
  'pendingResponses',
  'deadLetterQueue',
  'processingFailures',
  'total',
];

function count(value) {
  const normalized = Number(value);
  return Number.isFinite(normalized) && normalized >= 0 ? normalized : 0;
}

function normalizedCounts(value, keys) {
  return keys.reduce((result, key) => {
    result[key] = count(value?.[key]);
    return result;
  }, {});
}

function normalizedWarnings(value) {
  if (!Array.isArray(value)) return [];
  return value.map(warning => ({
    code: typeof warning?.code === 'string' ? warning.code : 'operations_warning',
    count: count(warning?.count),
    byCollection: normalizedCounts(warning?.byCollection, [
      'outbox',
      'pendingResponses',
      'deadLetterQueue',
      'processingFailures',
    ]),
  }));
}

function normalizedSupportIssues(value) {
  if (!Array.isArray(value)) return [];
  return value
    .filter(issue => ['deadLetterQueue', 'processingFailures'].includes(issue?.collection))
    .map(issue => ({
      id: String(issue?.id || ''),
      collection: issue.collection,
      clientId: typeof issue?.clientId === 'string' && issue.clientId ? issue.clientId : null,
      status: String(issue?.status || 'open').trim().toLowerCase() || 'open',
      source: typeof issue?.source === 'string' && issue.source ? issue.source : null,
      createdAt: issue?.createdAt || null,
      outsideLiveCampaign: issue?.outsideLiveCampaign === true,
      summary: typeof issue?.summary === 'string' && issue.summary
        ? issue.summary
        : 'Support review is required.',
    }));
}

function normalizeOperationsReport(data) {
  const users = Array.isArray(data?.users) ? data.users : [];
  return {
    summary: {
      userCount: count(data?.summary?.userCount),
      campaigns: normalizedCounts(data?.summary?.campaigns, CAMPAIGN_KEYS),
      actionable: normalizedCounts(data?.summary?.actionable, ACTIONABLE_KEYS),
      usersWithWarnings: count(data?.summary?.usersWithWarnings),
      warningCount: count(data?.summary?.warningCount),
      activeThreads: count(data?.summary?.activeThreads),
      lastActivityAt: data?.summary?.lastActivityAt || null,
    },
    users: users.map(user => ({
      uid: String(user?.uid || ''),
      email: typeof user?.email === 'string' ? user.email : null,
      displayName: typeof user?.displayName === 'string' ? user.displayName : null,
      campaigns: normalizedCounts(user?.campaigns, CAMPAIGN_KEYS),
      actionable: normalizedCounts(user?.actionable, ACTIONABLE_KEYS),
      activeThreads: count(user?.activeThreads),
      lastActivityAt: user?.lastActivityAt || null,
      warnings: normalizedWarnings(user?.warnings),
      supportIssues: normalizedSupportIssues(user?.supportIssues),
    })),
  };
}

export async function requestAdminOperationsReport({ firebaseFunctions = functions } = {}) {
  const callable = httpsCallable(firebaseFunctions, 'getAdminOperationsReport');
  const response = await callable();
  return normalizeOperationsReport(response?.data);
}

export async function requestCampaignAccessState({ firebaseFunctions = functions } = {}) {
  const callable = httpsCallable(firebaseFunctions, 'getCampaignAccessState');
  const response = await callable();
  return normalizeAccessState(response?.data);
}

export async function setCampaignAccessState({
  firebaseFunctions = functions,
  creationEnabled,
  automationEnabled,
} = {}) {
  const payload = {};
  if (creationEnabled !== undefined) {
    if (typeof creationEnabled !== 'boolean') {
      throw new TypeError('creationEnabled must be a boolean');
    }
    payload.creationEnabled = creationEnabled;
  }
  if (automationEnabled !== undefined) {
    if (typeof automationEnabled !== 'boolean') {
      throw new TypeError('automationEnabled must be a boolean');
    }
    payload.automationEnabled = automationEnabled;
  }
  if (!Object.keys(payload).length) {
    throw new TypeError('An access state boolean is required');
  }

  const callable = httpsCallable(firebaseFunctions, 'setCampaignAccessState');
  const response = await callable(payload);
  return normalizeAccessState(response?.data);
}

export { normalizeAccessState, normalizeOperationsReport };
